// Path: assets/ps-core.js
//
// Minimal core for Hybrid Dynamic PDP.
//
// What this file does now:
// - Defines PS namespace with tiny DOM helpers.
// - Manages ps_cc cookie: {make,color,code,hex,year,model,factory_code,make_slug,color_slug,ts}.
// - Exposes PS.ssr() to read window.PS_SSR injected by the edge.
//
// What was removed:
// - Client API fetching, URL parsing, and validation.
// - Client-side color painting. Edge handles head/body content.

(function(window, document){
  'use strict';

  // Namespace
  var PS = window.PS || (window.PS = {});

  // ---------------------------------------------------------------------------
  // Utils (tiny, ES5-safe)
  // ---------------------------------------------------------------------------
  PS.u = {
    prop: function(id){ return document.getElementById(id); },
    setText: function(el, txt){ if (el) el.textContent = txt != null ? String(txt) : ''; },
    safeHex: function(h){
      return String(h || '').replace('#','').trim().toLowerCase();
    },
    joinBadge: function(make, color, code){
      var parts = [];
      if (make) parts.push(String(make));
      if (color) parts.push(String(color));
      if (code) parts.push('(' + String(code) + ')');
      return parts.join(' ');
    },
    onReady: function(cb){
      if (!cb) return;
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', cb);
      else cb();
    },
    apiBase: function(){
      try {
        if (window.paintscratchTheme && window.paintscratchTheme.psUrl) {
          return String(window.paintscratchTheme.psUrl).replace(/\/+$/,'') + '/api/';
        }
      } catch(e){}
      // Fallback to production API if config is missing (race condition safety)
      return 'https://hub.paintscratch.com/api/';
    }
  };

  // ---------------------------------------------------------------------------
  // Read-only access to Edge SSR payload (if present)
  // ---------------------------------------------------------------------------
  PS.ssr = function(){
    // Worker may set window.PS_SSR = { valid, make, color, code, hex, image, make_slug, color_slug, product_slug, years[], models[], factory_codes[] }
    var o = window.PS_SSR || null;
    if (!o || typeof o !== 'object') return null;

    // Shallow sanitize expected fields
    var out = {};
    out.valid = !!o.valid;
    out.make = o.make || '';
    out.color = o.color || '';
    out.code = o.code || '';
    out.hex = PS.u.safeHex(o.hex || '');
    out.image = o.image || '';
    out.make_slug = o.make_slug || '';
    out.color_slug = o.color_slug || '';
    out.product_slug = o.product_slug || '';
    out.years = Array.isArray(o.years) ? o.years.slice(0) : [];
    out.models = Array.isArray(o.models) ? o.models.slice(0) : [];
    out.isGuaranteed = !!o.isGuaranteed;
    return out;
  };

  // ---------------------------------------------------------------------------
  // Cookie utils for browse state: ps_cc
  // Shape: {"make","model","color","code","hex","year","factory_code","make_slug","color_slug","vehicle_scope","ps_from_search","ts"}

  // ---------------------------------------------------------------------------
  PS.cc = (function(){
    var NAME = 'ps_cc';
    var MAX_AGE = 60 * 60 * 24 * 30; // seconds
    var variantsNormalized = false;

    function read(){
      var raw = String(document.cookie || '').split(';');
      var best = null;
      var bestTs = -1;
      var i, part, value, parsed, ts;
      for (i = 0; i < raw.length; i++) {
        part = raw[i].replace(/^\s+|\s+$/g, '');
        if (part.indexOf(NAME + '=') !== 0) continue;
        value = part.substring(NAME.length + 1);
        try { parsed = JSON.parse(decodeURIComponent(value || '')); }
        catch(e) { parsed = null; }
        if (!parsed || typeof parsed !== 'object') continue;
        ts = Number(parsed.ts);
        if (!best || (isFinite(ts) && ts > bestTs)) {
          best = parsed;
          bestTs = isFinite(ts) ? ts : bestTs;
        }
      }
      return best;
    }

    function readForColor(makeSlug, colorSlug){
      var targetMake = String(makeSlug || '').toLowerCase();
      var targetColor = String(colorSlug || '').toLowerCase();
      var raw = String(document.cookie || '').split(';');
      var best = null;
      var bestTrusted = false;
      var bestTs = -1;
      var i, part, value, parsed, parsedMake, parsedColor, trusted, ts;
      if (!targetMake || !targetColor) return null;
      for (i = 0; i < raw.length; i++) {
        part = raw[i].replace(/^\s+|\s+$/g, '');
        if (part.indexOf(NAME + '=') !== 0) continue;
        value = part.substring(NAME.length + 1);
        try { parsed = JSON.parse(decodeURIComponent(value || '')); }
        catch(e) { parsed = null; }
        if (!parsed || typeof parsed !== 'object') continue;
        parsedMake = String(parsed.make_slug || '').toLowerCase();
        parsedColor = String(parsed.color_slug || '').toLowerCase();
        if (parsedMake !== targetMake || parsedColor !== targetColor) continue;
        trusted = !!(
          (parsed.vehicle_scope === true || String(parsed.vehicle_scope || '').toLowerCase() === 'true') &&
          String(parsed.year || '')
        );
        ts = Number(parsed.ts);
        if (!best || (trusted && !bestTrusted) || (trusted === bestTrusted && isFinite(ts) && ts > bestTs)) {
          best = parsed;
          bestTrusted = trusted;
          bestTs = isFinite(ts) ? ts : bestTs;
        }
      }
      return best;
    }

    function cookieHost(){
      return location.hostname || '';
    }

    function cookieParent(host){
      var parts = String(host || '').split('.');
      return parts.length > 2 ? '.' + parts.slice(-2).join('.') : host;
    }

    function expireVariant(path, domain){
      var expired = [NAME + '=', 'Path=' + path, 'Max-Age=0', 'SameSite=Lax'];
      if (location.protocol === 'https:') expired.push('Secure');
      if (domain) expired.push('Domain=' + domain);
      document.cookie = expired.join('; ');
    }

    function colorRoute(){
      var pathname = String(location.pathname || '/').split('?')[0];
      return pathname.match(/^\/colors\/([^\/]+)\/([^\/]+)\/(pen|bottle|spray|pro|kit-pen|kit-bottle|kit-spray)\/?$/);
    }

    function colorCleanupKey(routeMatch){
      if (!routeMatch) return '';
      return 'ps_cc_cleanup_v1:' + String(routeMatch[1]).toLowerCase() + '/' + String(routeMatch[2]).toLowerCase();
    }

    function visiblePsCcCandidateCount(){
      var raw = String(document.cookie || '').split(';');
      var count = 0;
      var i, part;
      for (i = 0; i < raw.length; i++) {
        part = raw[i].replace(/^\s+|\s+$/g, '');
        if (part.indexOf(NAME + '=') === 0) count++;
      }
      return count;
    }

    function shouldExpireVisibleVariants(key){
      var storage = null;
      if (!key) return true;
      try {
        storage = window.sessionStorage;
        return !storage || storage.getItem(key) !== '1';
      } catch(_e) {
        return true;
      }
    }

    function markVisibleVariantsExpired(key){
      if (!key) return;
      try {
        if (window.sessionStorage) window.sessionStorage.setItem(key, '1');
      } catch(_e) {}
    }

    function expireVisibleVariants(routeMatch, preserveCanonicalRoot){
      var pathname = String(location.pathname || '/').split('?')[0];
      var segments = pathname.split('/');
      var paths = ['/'];
      var host = cookieHost();
      var parent = cookieParent(host);
      var domains = ['', host];
      var current = '';
      var siblingProducts = ['pen', 'bottle', 'spray', 'pro', 'kit-pen', 'kit-bottle', 'kit-spray'];
      var i, j, k, basePath;
      routeMatch = routeMatch || colorRoute();
      function addPath(path){
        for (k = 0; k < paths.length; k++) {
          if (paths[k] === path) return;
        }
        paths.push(path);
      }
      if (parent && parent !== host) domains.push(parent);
      for (i = 1; i < segments.length; i++) {
        if (!segments[i]) continue;
        current += '/' + segments[i];
        addPath(current);
        if (i < segments.length - 1) addPath(current + '/');
      }
      if (routeMatch) {
        basePath = '/colors/' + routeMatch[1] + '/' + routeMatch[2];
        for (i = 0; i < siblingProducts.length; i++) {
          addPath(basePath + '/' + siblingProducts[i]);
          addPath(basePath + '/' + siblingProducts[i] + '/');
        }
      }
      for (i = 0; i < paths.length; i++) {
        for (j = 0; j < domains.length; j++) {
          if (preserveCanonicalRoot && paths[i] === '/' && domains[j] === parent) continue;
          expireVariant(paths[i], domains[j]);
        }
      }
    }

    function normalizeColorContext(){
      var routeMatch = colorRoute();
      var cleanupKey = '';
      if (!routeMatch) return false;
      cleanupKey = colorCleanupKey(routeMatch);
      try {
        if (shouldExpireVisibleVariants(cleanupKey) || visiblePsCcCandidateCount() > 1) {
          expireVisibleVariants(routeMatch, true);
          if (visiblePsCcCandidateCount() <= 1) markVisibleVariantsExpired(cleanupKey);
        }
        variantsNormalized = true;
        return true;
      } catch(_e) {
        return false;
      }
    }

function write(obj){
    if (!obj) return;
    var existing = read() || {};
    var now = Date.now();

    function norm(v){ return (v == null ? '' : String(v)).trim(); }

    var incMake        = obj.hasOwnProperty('make')         ? norm(obj.make)        : null;
    var incModel       = obj.hasOwnProperty('model')        ? norm(obj.model)       : null;
    var incColor       = obj.hasOwnProperty('color')        ? norm(obj.color)       : null;
    var incCode        = obj.hasOwnProperty('code')         ? norm(obj.code)        : null;
    var incHex         = obj.hasOwnProperty('hex')          ? PS.u.safeHex(obj.hex) : null;
    var incFactoryCode = obj.hasOwnProperty('factory_code') ? norm(obj.factory_code) : null;
    var incMakeSlug    = obj.hasOwnProperty('make_slug')    ? norm(obj.make_slug).toLowerCase()  : null;
    var incColorSlug   = obj.hasOwnProperty('color_slug')   ? norm(obj.color_slug).toLowerCase() : null;
    var incVehicleScope = obj.hasOwnProperty('vehicle_scope') ? (obj.vehicle_scope === true || norm(obj.vehicle_scope) === 'true') : null;
    var incFromSearch = obj.hasOwnProperty('ps_from_search') ? (obj.ps_from_search === true || norm(obj.ps_from_search) === 'true') : null;
    if (incVehicleScope === null && obj.hasOwnProperty('ps_from_search')) {
      incVehicleScope = obj.ps_from_search === true || norm(obj.ps_from_search) === 'true';
    }

    var changedKey =
      (incMake  != null && norm(existing.make)  !== incMake)  ||
      (incColor != null && norm(existing.color) !== incColor) ||
      (incCode  != null && norm(existing.code)  !== incCode);
    var slugChanged =
      (incMakeSlug  != null && norm(existing.make_slug).toLowerCase()  !== incMakeSlug) ||
      (incColorSlug != null && norm(existing.color_slug).toLowerCase() !== incColorSlug);
    var vehicleContextChanged = changedKey || slugChanged;
    var incomingColorContext = !!(
      (incMake != null || incMakeSlug != null) &&
      ((incColor != null && incColor !== '') || (incCode != null && incCode !== '') || (incColorSlug != null && incColorSlug !== ''))
    );
    var allowVehicleFields = !vehicleContextChanged || !incomingColorContext || incVehicleScope === true;

    var contextChanged = vehicleContextChanged ||
      (incModel != null && norm(existing.model) !== incModel) ||
      (obj.hasOwnProperty('year') && norm(existing.year) !== norm(obj.year));

    var yearVal;
    if (obj.hasOwnProperty('year') && allowVehicleFields) {
      yearVal = norm(obj.year);
    } else if (vehicleContextChanged) {
      yearVal = '';
    } else {
      yearVal = norm(existing.year);
    }

    var payload = {
      make:         incMake        != null ? incMake        : norm(existing.make),
      model:        incModel       != null && allowVehicleFields ? incModel : (vehicleContextChanged ? '' : norm(existing.model)),
      color:        incColor       != null ? incColor       : norm(existing.color),
      code:         incCode        != null ? incCode        : norm(existing.code),
      hex:          incHex         != null ? incHex         : PS.u.safeHex(existing.hex),
      year:         yearVal,
      factory_code: incFactoryCode != null && allowVehicleFields ? incFactoryCode : (contextChanged ? '' : norm(existing.factory_code)),
      make_slug:    incMakeSlug    != null ? incMakeSlug    : (changedKey ? '' : norm(existing.make_slug)),
      color_slug:   incColorSlug   != null ? incColorSlug   : (changedKey ? '' : norm(existing.color_slug)),
      vehicle_scope: allowVehicleFields ? (incVehicleScope != null ? incVehicleScope : existing.vehicle_scope === true) : false,
      ps_from_search: allowVehicleFields ? (incFromSearch != null ? incFromSearch : existing.ps_from_search === true) : false,
      ts:           now
    };

    var v = encodeURIComponent(JSON.stringify(payload));
    var base = [
      NAME + '=' + v,
      'Path=/',
      'Max-Age=' + MAX_AGE,
      'SameSite=Lax'
    ];
    var host = cookieHost();
    var parent = cookieParent(host);
    if (location.protocol === 'https:') base.push('Secure');

    // Retire route- and host-scoped legacy variants before writing one root cookie.
    if (!variantsNormalized) {
      try {
        if (!normalizeColorContext()) {
          expireVisibleVariants(null, false);
          variantsNormalized = true;
        }
      } catch(_e2) { variantsNormalized = true; }
    }
    try {
      document.cookie = base.join('; ') + '; Domain=' + parent;
    } catch(_e2){
      document.cookie = base.join('; ');
    }
}



    function clear(){
      document.cookie = NAME + '=; Path=/; Max-Age=0; SameSite=Lax';
    }

    function isStale(obj){
      if (!obj || !obj.ts) return true;
      return (Date.now() - obj.ts) > (MAX_AGE * 1000);
    }

    function getYear(){
      var c = read(); return c && c.year ? String(c.year) : '';
    }
    function setYear(y){
      if (y == null || y === '') { write({ year: '' }); return; }
      write({ year: String(y) });
    }
    function clearYear(){ write({ year: '' }); }

    return {
      NAME: NAME,
      read: read,
      readForColor: readForColor,
      write: write,
      normalizeColorContext: normalizeColorContext,
      clear: clear,
      isStale: isStale,
      getYear: getYear,
      setYear: setYear,
      clearYear: clearYear
    };
  })();

  // ---------------------------------------------------------------------------
  // Header Color Hub link
  // ---------------------------------------------------------------------------
  PS.syncHeaderColorHubLinks = (function(){
    var selector = '#HeaderMenu-shop-all-products, #HeaderDrawer-shop-all-products';
    var runtimeContext = null;
    var runtimeContextSet = false;

    function normalizeContext(value){
      var makeSlug = String(value && value.make_slug || '').replace(/^\s+|\s+$/g, '').toLowerCase();
      var colorSlug = String(value && value.color_slug || '').replace(/^\s+|\s+$/g, '').toLowerCase();
      if (!/^[a-z0-9][a-z0-9-]*$/.test(makeSlug) || !/^[a-z0-9][a-z0-9-]*$/.test(colorSlug)) return null;
      return { make_slug: makeSlug, color_slug: colorSlug };
    }

    function currentContext(eventDetail, preferCookie){
      var context = normalizeContext(eventDetail);
      var ssr = null;
      var cc = null;
      if (context) return context;
      if (runtimeContextSet) return runtimeContext;
      try { cc = PS.cc && PS.cc.read ? PS.cc.read() : null; } catch (_e) { cc = null; }
      if (preferCookie) {
        context = normalizeContext(cc);
        if (context) return context;
      }
      try { ssr = PS.ssr ? PS.ssr() : null; } catch (_e2) { ssr = null; }
      if (ssr && ssr.valid) {
        context = normalizeContext(ssr);
        if (context) return context;
      }
      return normalizeContext(cc);
    }

    function sync(eventDetail, forceGeneric, preferCookie){
      var links = null;
      var context = forceGeneric ? null : currentContext(eventDetail, preferCookie);
      var href = context ? '/colors/' + encodeURIComponent(context.make_slug) + '/' + encodeURIComponent(context.color_slug) + '/' : '';
      var i;
      var link;
      var defaultHref;
      try { links = document.querySelectorAll(selector); } catch (_e) { links = null; }
      if (!links) return;
      for (i = 0; i < links.length; i++) {
        link = links[i];
        if (!link || !link.getAttribute || !link.setAttribute) continue;
        defaultHref = link.getAttribute('data-ps-default-href');
        if (defaultHref == null) {
          defaultHref = link.getAttribute('href') || '';
          link.setAttribute('data-ps-default-href', defaultHref);
        }
        link.setAttribute('href', href || defaultHref);
      }
    }

    function isHeaderLinkTarget(target){
      var id = '';
      while (target && target !== document) {
        id = target.getAttribute ? String(target.getAttribute('id') || '') : '';
        if (id === 'HeaderMenu-shop-all-products' || id === 'HeaderDrawer-shop-all-products') return true;
        target = target.parentNode;
      }
      return false;
    }

    PS.u.onReady(function(){
      sync();
      document.addEventListener('ps:color:validated', function(event){
        runtimeContext = normalizeContext(event && event.detail ? event.detail : null);
        runtimeContextSet = true;
        sync(runtimeContext, !runtimeContext);
      }, false);
      document.addEventListener('ps:color:cleared', function(){ runtimeContext = null; runtimeContextSet = true; sync(null, true); }, false);
      document.addEventListener('ps:color:invalid', function(){ runtimeContext = null; runtimeContextSet = true; sync(null, true); }, false);
      document.addEventListener('click', function(event){
        if (isHeaderLinkTarget(event && (event.target || event.srcElement))) sync();
      }, true);
    });
    if (window.addEventListener) {
      window.addEventListener('pageshow', function(event){
        var path = String(window.location && window.location.pathname || '');
        if (!event || !event.persisted) return;
        runtimeContext = null;
        runtimeContextSet = false;
        sync(null, false, path.indexOf('/colors/') !== 0);
      }, false);
    }

    return sync;
  })();

  // ---------------------------------------------------------------------------
  // Shared API Client (ES5)
  // Centralizes calls to Legacy/Shopify APIs
  // ---------------------------------------------------------------------------
  PS.api = (function(){
    function req(endpoint, params, cb) {
      var url = PS.u.apiBase() + endpoint;
      if (params) {
        var parts = [];
        for (var k in params) {
          if (params.hasOwnProperty(k) && params[k] !== null && params[k] !== '') {
            parts.push(encodeURIComponent(k) + '=' + encodeURIComponent(params[k]));
          }
        }
        if (parts.length) url += (url.indexOf('?') === -1 ? '?' : '&') + parts.join('&');
      }

      var xhr = new XMLHttpRequest();
      xhr.open('GET', url, true);
      xhr.onreadystatechange = function() {
        if (xhr.readyState !== 4) return;
        var data = null;
        if (xhr.status >= 200 && xhr.status < 300) {
          try { data = JSON.parse(xhr.responseText); } catch(e){}
        }
        if (cb) cb(data, xhr.status);
      };
      xhr.send(null);
    }

    return {
      // GET /api/shopify/colors/{make}?page=X&q=query
      getColors: function(make, options, cb) {
        // Support legacy signature: getColors(make, pageNumber, cb)
        var params = (typeof options === 'object') ? options : { page: options };
        
        // Set defaults if not provided
        if (!params.per_page) params.per_page = 16;
        req('shopify/colors/' + encodeURIComponent(String(make).toLowerCase()), params, cb);
      },
      // GET /api/shopify/cars/{make}/{color}
      getColorDetails: function(makeSlug, colorSlug, cb) {
        req('shopify/cars/' + encodeURIComponent(makeSlug) + '/' + encodeURIComponent(colorSlug), null, cb);
      }
    };
  })();

})(window, document);
